import {
    Box,
    Button,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { CampaignCategoryHumanizedNames } from 'apis/ayceCampaign';
import { requestVendors } from 'application/config/vendorsStore';
import { fetchAyceCampaigns } from 'application/vouchers/ayceSlice';
import { CustomTableCell } from 'components/common/CustomTable/CustomTableCell';
import { Loader } from 'components/common/Loader/Loader';
import { format as formatDate, isPast } from 'date-fns';
import { isLoading, isNotRequested } from 'helpers/requestStateHelper';
import { Vendor } from 'models/vendor';
import * as React from 'react';
import { useEffect } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { routes } from 'routes';
import { useDispatch, useSelector } from 'store';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        wrapper: { margin: '44.5px' },
        header: {
            display: 'flex',
            flexDirection: 'row',
        },
        topActionBar: {
            height: 'fit-content',
            marginLeft: 'auto',
            marginRight: 0,
            alignSelf: 'center',
        },
        addButton: {
            marginLeft: theme.spacing(1),
        },
        headerTitle: {
            fontSize: '2rem',
        },
        vendorLink: {
            display: 'inline-block',
            margin: `0 ${theme.spacing(1)}px`,
            '&:after': {
                content: '"/"',
                position: 'absolute',
                paddingLeft: theme.spacing(0.5),
                color: theme.palette.grey[500],
            },
            '&:last-child:after': {
                content: '""',
            },
        },
        tableRow: {
            '&:nth-of-type(odd)': {
                backgroundColor: theme.palette.action.selected,
            },
        },
        status_expired: {
            color: theme.palette.grey[500],
        },
        status_draft: {
            color: theme.palette.warning.dark,
        },
        status_published: {
            color: theme.palette.success.dark,
        },
        status_unpublished: {
            color: theme.palette.error.dark,
        },
    }),
);

export default function AyceCampaigns(): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const vendors: { data: Vendor[]; requestState: string } = useSelector(state => state.apiConfig.vendors);
    const campaigns = useSelector(state => state.vouchers.ayce.list);

    const dispatch = useDispatch();

    const onAddCampaign = () => history.push(routes.newAyceCampaign);

    useEffect(() => {
        dispatch(fetchAyceCampaigns());

        if (isNotRequested(vendors)) {
            dispatch(requestVendors());
        }
    }, []);

    if (isLoading(vendors) || isLoading(campaigns)) {
        return <Loader />;
    }

    const { payload: list } = campaigns;

    return (
        <Box className={classes.wrapper}>
            <Box className={classes.header}>
                <Typography variant="subtitle1" component="h1" className={classes.headerTitle}>
                    AYCE Campaigns
                </Typography>
                <Box className={classes.topActionBar}>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.addButton}
                        data-cy="Ayce-AddCampaignButton"
                        onClick={onAddCampaign}
                    >
                        Add Campaign
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper} data-cy="Ayce-CampaignsTable">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <CustomTableCell>Campaign name</CustomTableCell>
                            <CustomTableCell width="110">Category</CustomTableCell>
                            <CustomTableCell width="130">End Date</CustomTableCell>
                            <CustomTableCell>Vendors</CustomTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.map(row => {
                            const vendorsToShow = vendors.data
                                .map(vendor => {
                                    const match = row.vendorCampaignList.find(v => v.vendorName === vendor.name);
                                    return {
                                        ...vendor,
                                        status: match?.status,
                                    };
                                })
                                .filter(vendor => !!vendor.status);

                            const campaignLink = routes.editAyceCampaign.replace(':campaignId', row.campaignId);
                            const expiredClass = isPast(new Date(row.endDate)) ? classes.status_expired : '';

                            return (
                                <TableRow key={row.campaignId} className={classes.tableRow} data-cy="Ayce-CampaignRow">
                                    <TableCell>
                                        <Link
                                            component={RouterLink}
                                            to={campaignLink}
                                            variant="body2"
                                            color="primary"
                                            className={`${classes.vendorLink} ${expiredClass}`}
                                            data-cy="Ayce-CampaignEditLink"
                                        >
                                            {row.name}
                                        </Link>
                                    </TableCell>
                                    <TableCell className={expiredClass}>
                                        {CampaignCategoryHumanizedNames[row.category]}
                                    </TableCell>
                                    <TableCell className={expiredClass}>
                                        {formatDate(new Date(row.endDate), 'yyyy-LL-dd')}
                                    </TableCell>
                                    <TableCell>
                                        {vendorsToShow.map(vendor => (
                                            <Link
                                                component={RouterLink}
                                                key={vendor.name}
                                                to={campaignLink + `?tab=${vendor.name}`}
                                                variant="body2"
                                                color="primary"
                                                className={[
                                                    classes.vendorLink,
                                                    expiredClass || classes['status_' + vendor.status],
                                                ].join(' ')}
                                                data-cy="Ayce-CampaignEditLink"
                                            >
                                                {vendor.country}
                                            </Link>
                                        ))}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
